import React, { useState, useEffect } from 'react';
import { AlertTriangle } from 'lucide-react';
import Modal from '../ui/Modal';
import Button from '../ui/Button';
import { useUpdateApiKey, useApiKeyAbilities } from '../../hooks/useApiKeys';
import { useLanguage } from '../../lang/LangContext';
import { KEYS } from '../../lang/keys';
import { normalizeFieldErrors } from '../../services/api';

const RESOURCE_LABELS = {
    supplier: 'Suppliers',
    supplierCredential: 'Supplier Credentials',
    service: 'Services',
    invoice: 'Invoices',
    invoiceTemplate: 'Invoice Templates',
    reading: 'Readings',
    readingTemplate: 'Reading Templates',
    file: 'Files',
    scheduled_action: 'Scheduled Actions',
    reminder: 'Reminders',
    user: 'User',
};

const groupAbilities = (abilities) => {
    const groups = {};
    abilities.forEach((ability) => {
        if (ability === '*') return;
        const prefix = ability.split(':')[0];
        if (!groups[prefix]) groups[prefix] = [];
        groups[prefix].push(ability);
    });
    return groups;
};

const toDateString = (isoString) => {
    if (!isoString) return '';
    return isoString.slice(0, 10);
};

const EditApiKeyModal = ({ isOpen, onClose, apiKey, onRegenerated }) => {
    const [formData, setFormData] = useState({ abilities: [], full_access: false, expires_at: '' });
    const [errors, setErrors] = useState({});
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [submitError, setSubmitError] = useState('');

    const updateApiKey = useUpdateApiKey();
    const { data: allAbilities = [], isLoading: abilitiesLoading } = useApiKeyAbilities();
    const { t } = useLanguage();

    const abilityGroups = groupAbilities(allAbilities);

    useEffect(() => {
        if (apiKey && isOpen) {
            const isFullAccess = apiKey.abilities?.includes('*');
            setFormData({
                full_access: isFullAccess,
                abilities: isFullAccess ? [] : (apiKey.abilities ?? []),
                expires_at: toDateString(apiKey.expires_at),
            });
        }
    }, [apiKey, isOpen]);

    const validateForm = () => {
        const newErrors = {};
        if (!formData.full_access && formData.abilities.length === 0) {
            newErrors.abilities = t(KEYS.API_KEY_ABILITIES_REQUIRED);
        }
        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleFullAccessToggle = (e) => {
        setFormData((prev) => ({ ...prev, full_access: e.target.checked, abilities: [] }));
        if (errors.abilities) setErrors((prev) => ({ ...prev, abilities: '' }));
    };

    const handleAbilityToggle = (ability) => {
        setFormData((prev) => {
            const next = prev.abilities.includes(ability)
                ? prev.abilities.filter((a) => a !== ability)
                : [...prev.abilities, ability];
            return { ...prev, abilities: next };
        });
        if (errors.abilities) setErrors((prev) => ({ ...prev, abilities: '' }));
    };

    const handleExpiresChange = (e) => {
        setFormData((prev) => ({ ...prev, expires_at: e.target.value }));
    };

    const handleClearExpiry = () => {
        setFormData((prev) => ({ ...prev, expires_at: '' }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setSubmitError('');
        if (!validateForm()) return;

        setIsSubmitting(true);
        try {
            const payload = {
                abilities: formData.full_access ? ['*'] : formData.abilities,
                expires_at: formData.expires_at
                    ? new Date(formData.expires_at).toISOString()
                    : null,
            };
            const result = await updateApiKey.mutateAsync({ id: apiKey.id, data: payload });
            onRegenerated({ token: result.token, apiKey: result.api_key });
            onClose();
        } catch (error) {
            if (error.code === 'VALIDATION_ERROR' && error.errors) {
                setErrors(normalizeFieldErrors(error.errors));
            } else {
                setSubmitError(error.message || t(KEYS.API_KEY_EDIT_ERROR));
            }
        } finally {
            setIsSubmitting(false);
        }
    };

    const handleClose = () => {
        if (!isSubmitting) {
            setErrors({});
            setSubmitError('');
            onClose();
        }
    };

    const minDate = new Date(Date.now() + 86400000).toISOString().slice(0, 10);

    return (
        <Modal
            isOpen={isOpen}
            onClose={handleClose}
            title={t(KEYS.API_KEY_EDIT_TITLE)}
            subtitle={apiKey?.name}
            size="large"
            footer={
                <>
                    <Button variant="outline" size="medium" onClick={handleClose} disabled={isSubmitting}>
                        {t(KEYS.COMMON_CANCEL)}
                    </Button>
                    <Button variant="primary" size="medium" onClick={handleSubmit} disabled={isSubmitting}>
                        {isSubmitting ? t(KEYS.API_KEY_EDIT_SUBMITTING) : t(KEYS.API_KEY_EDIT_SUBMIT)}
                    </Button>
                </>
            }
        >
            {submitError && (
                <div className="p-3 mb-4 bg-status-overdue-bg text-status-overdue rounded text-sm">
                    {submitError}
                </div>
            )}

            <div className="space-y-5">
                {/* Warning */}
                <div className="flex items-start gap-3 p-3 bg-status-overdue-bg rounded border border-status-overdue/20">
                    <AlertTriangle className="w-4 h-4 text-status-overdue flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-status-overdue">
                        {t(KEYS.API_KEY_REGEN_WARNING)}
                    </p>
                </div>

                {/* Expiry */}
                <div>
                    <label className="label-base block mb-1.5">
                        {t(KEYS.API_KEY_FIELD_EXPIRY)} <span className="text-text-muted text-[10px] font-normal ml-1">{t(KEYS.API_KEY_FIELD_EXPIRY_OPTIONAL)}</span>
                    </label>
                    <div className="flex items-center gap-2">
                        <input
                            type="date"
                            value={formData.expires_at}
                            onChange={handleExpiresChange}
                            min={minDate}
                            className="bg-surface-overlay border border-surface-border rounded px-3 py-2 text-sm text-text-primary focus:outline-none focus:ring-1 focus:ring-accent focus:border-accent transition-colors"
                        />
                        {formData.expires_at && (
                            <button
                                type="button"
                                onClick={handleClearExpiry}
                                className="text-xs text-text-muted hover:text-status-overdue transition-colors"
                            >
                                Clear
                            </button>
                        )}
                    </div>
                    <p className="mt-1 text-xs text-text-muted">{t(KEYS.API_KEY_FIELD_EXPIRY_REMOVE_HINT)}</p>
                </div>

                {/* Abilities */}
                <div>
                    <label className="label-base block mb-2">
                        {t(KEYS.API_KEY_FIELD_ABILITIES)} <span className="text-status-overdue">*</span>
                    </label>

                    <label className="flex items-center gap-2.5 cursor-pointer mb-3 p-2.5 rounded border border-accent/30 bg-accent/5">
                        <input
                            type="checkbox"
                            checked={formData.full_access}
                            onChange={handleFullAccessToggle}
                            className="w-4 h-4 accent-accent rounded cursor-pointer"
                        />
                        <div>
                            <span className="text-sm font-medium text-accent">{t(KEYS.API_KEY_FULL_ACCESS_LABEL)}</span>
                            <p className="text-xs text-text-muted">{t(KEYS.API_KEY_FULL_ACCESS_DESC)}</p>
                        </div>
                    </label>

                    {errors.abilities && (
                        <p className="mb-2 text-xs text-status-overdue">{errors.abilities}</p>
                    )}

                    {abilitiesLoading ? (
                        <p className="text-sm text-text-muted">{t(KEYS.API_KEY_LOADING_ABILITIES)}</p>
                    ) : (
                        <div className={`space-y-3 ${formData.full_access ? 'opacity-40 pointer-events-none select-none' : ''}`}>
                            {Object.entries(abilityGroups).map(([prefix, abilities]) => (
                                <div key={prefix} className="border border-surface-border rounded overflow-hidden">
                                    <div className="px-3 py-1.5 bg-surface-overlay border-b border-surface-border">
                                        <span className="text-[10px] font-semibold text-text-muted uppercase tracking-widest">
                                            {RESOURCE_LABELS[prefix] ?? prefix}
                                        </span>
                                    </div>
                                    <div className="px-3 py-2 flex flex-wrap gap-x-4 gap-y-1.5">
                                        {abilities.map((ability) => {
                                            const action = ability.split(':')[1];
                                            return (
                                                <label
                                                    key={ability}
                                                    className="flex items-center gap-1.5 cursor-pointer"
                                                >
                                                    <input
                                                        type="checkbox"
                                                        checked={formData.abilities.includes(ability)}
                                                        onChange={() => handleAbilityToggle(ability)}
                                                        className="w-3.5 h-3.5 accent-accent rounded cursor-pointer"
                                                    />
                                                    <span className="text-xs text-text-secondary capitalize">
                                                        {action}
                                                    </span>
                                                </label>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    )}
                </div>
            </div>
        </Modal>
    );
};

export default EditApiKeyModal;
